import styled from 'styled-components';
import * as React from 'react';
import { Variant } from '@material-ui/core/styles/createTypography';
import { NotEnoughDataMessage } from '../NotEnoughDataMessage';
import { DSTypography } from '../../mui';
import { OverlayLoading } from '../..';
import { ErrorMessage } from '../../ErrorMessage';
interface StyledSectionProps extends React.HTMLAttributes<HTMLDivElement> {
    flex?: boolean;
    avoidPrintPageBreak?: boolean;
}

const StyledSection = styled.div<StyledSectionProps>`
    position: relative;

    ${({ flex }) =>
        flex
            ? `
        display: flex;
        flex-direction: column;
        height: 100%;
    `
            : ''}

    margin-bottom: 20px;

    @media print {
        ${({ avoidPrintPageBreak }) => (avoidPrintPageBreak ? 'page-break-inside: avoid;' : '')}
    }
`;

interface StyledWrapperDivProps extends React.HTMLAttributes<HTMLDivElement> {
    flex?: boolean;
    padChildren?: boolean;
}

const StyledWrapperDiv = styled.div<StyledWrapperDivProps>`
    // Make the overlay loader relative to this div
    position: relative;

    ${({ flex }) =>
        flex
            ? `
        display: flex;
        flex-direction: column;
        height: 100%;
    `
            : ''}

    // Use this so the overlay loader isn't outside the box
    min-height: 60px;

    ${({ padChildren }) =>
        padChildren
            ? `
                padding: 5px;
            `
            : ''}
`;

const StyledHeaderWrapperDiv = styled.div`
    display: flex;
    align-items: center;

    margin-bottom: 10px;
`;

const StyledHeaderRightContent = styled.span`
    margin-left: auto;
`;

export interface ReportSectionProps extends Omit<StyledWrapperDivProps, 'title'> {
    controls?: JSX.Element;
    error?: Error;
    hasEnoughData?: boolean;
    loading?: boolean;
    title?: JSX.Element | string;
    titleVariant?: Variant;
    flex?: boolean;
    avoidPrintPageBreak?: boolean;
}

export const ReportSection: React.FunctionComponent<ReportSectionProps> = ({
    avoidPrintPageBreak = true,
    children,
    controls,
    error,
    flex = false,
    hasEnoughData,
    loading,
    padChildren = true,
    title,
    titleVariant = 'h5',
    ...props
}) => (
    <StyledSection avoidPrintPageBreak={avoidPrintPageBreak} flex={flex}>
        {(title || controls) && (
            <StyledHeaderWrapperDiv>
                {title && <DSTypography variant={titleVariant}>{title}</DSTypography>}
                {controls && <StyledHeaderRightContent>{controls}</StyledHeaderRightContent>}
            </StyledHeaderWrapperDiv>
        )}
        <StyledWrapperDiv flex={flex} padChildren={padChildren} {...props}>
            {children}
            {loading && <OverlayLoading disableInteraction />}
            {!loading && hasEnoughData === false && typeof error === 'undefined' && <NotEnoughDataMessage />}
            {typeof error !== 'undefined' && (
                <ErrorMessage
                    message={error.message || 'The server responded with an error'}
                    title="Error loading the data"
                />
            )}
        </StyledWrapperDiv>
    </StyledSection>
);
ReportSection.displayName = 'ReportSection';
